<template>
  <div id="home-app">

    <!-- ══════════════════════════════════════════════════
         NAVIGATION
    ══════════════════════════════════════════════════ -->
    <nav class="nav" :class="{ scrolled: isScrolled }">
      <div class="container nav-inner">
        <div class="nav-logo">
          <svg class="nav-logo-svg" viewBox="0 0 40 40" fill="none">
            <defs>
              <linearGradient id="nv-lg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#667eea"/>
                <stop offset="100%" stop-color="#764ba2"/>
              </linearGradient>
            </defs>
            <path d="M6 8h14l12 12-12 12H6l12-12L6 8z" fill="url(#nv-lg)"/>
            <circle cx="35" cy="10" r="2.5" fill="url(#nv-lg)"/>
            <circle cx="35" cy="30" r="2.5" fill="url(#nv-lg)"/>
          </svg>
          <div class="nav-logo-text">
            <span class="nav-logo-name">Nick Volkmann</span>
            <span class="nav-logo-sub">IT-Beratung</span>
          </div>
        </div>

        <div class="nav-links" :class="{ open: mobileMenuOpen }">
          <a @click.prevent="nav('home')">Start</a>
          <a @click.prevent="nav('features')">Funktionen</a>
          <a @click.prevent="nav('portfolio')">Referenzen</a>
          <a @click.prevent="nav('contact')">Kontakt</a>
          <router-link to="/login" class="nav-cta-btn" @click="mobileMenuOpen = false">
            Login
          </router-link>
        </div>

        <button class="hamburger" :class="{ open: mobileMenuOpen }" @click="toggleMenu" aria-label="Menu öffnen">
          <span></span><span></span><span></span>
        </button>
      </div>
    </nav>

    <!-- ══════════════════════════════════════════════════
         HERO
    ══════════════════════════════════════════════════ -->
    <section id="home" class="hero">
      <div class="hero-bg">
        <div class="hero-grid-overlay"></div>
        <div class="hero-glow glow-1"></div>
        <div class="hero-glow glow-2"></div>
      </div>

      <div class="container hero-inner">
        <!-- Left: Copy -->
        <div class="hero-text">
          <div class="available-badge">
            <span class="badge-pulse"></span>
            Verfügbar für neue Projekte
          </div>
          <h1 class="hero-h1">
            <span>Professionelles</span>
            <span class="hero-accent">Terminbuchungssystem</span>
            <span>mit KI-Assistent</span>
          </h1>
          <p class="hero-sub">
            Vollautomatisierte Buchungslösung mit Google&nbsp;Calendar&#8209;Integration,
            KI&#8209;Telefonassistent, Admin&#8209;Dashboard und Payment&#8209;Anbindung –
            maßgeschneidert für Ihr Unternehmen.
          </p>
          <div class="hero-actions">
            <button @click="scrollTo('contact')" class="btn btn-primary">
              Jetzt anfragen
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <button @click="scrollTo('features')" class="btn btn-ghost">
              Features entdecken
            </button>
          </div>
          <div class="hero-pills">
            <span class="pill">✓ KI-Telefon-Assistent</span>
            <span class="pill">✓ Google Calendar</span>
            <span class="pill">✓ WhatsApp</span>
            <span class="pill">✓ PayPal & Stripe</span>
          </div>
        </div>

        <!-- Right: Interactive Booking Mockup -->
        <div class="hero-mockup">
          <div class="mockup-window">
            <div class="mockup-bar">
              <span class="dot dot-r"></span>
              <span class="dot dot-y"></span>
              <span class="dot dot-g"></span>
              <span class="mockup-bar-title">Online-Terminbuchung</span>
            </div>
            <div class="mockup-body">

              <!-- Step 0: Calendar -->
              <transition name="step-fade" mode="out-in">
                <div v-if="heroStep === 0" key="cal" class="mock-cal">
                  <div class="mock-cal-nav">
                    <button class="cal-nav-btn" @click="prevMonth">‹</button>
                    <span class="mock-cal-title">{{ heroMonthLabel }}</span>
                    <button class="cal-nav-btn" @click="nextMonth">›</button>
                  </div>
                  <div class="mock-cal-weekdays">
                    <span v-for="d in weekDays" :key="d">{{ d }}</span>
                  </div>
                  <div class="mock-cal-grid">
                    <div
                      v-for="(day, i) in calendarDays"
                      :key="i"
                      class="cal-cell"
                      :class="{
                        empty: !day,
                        past: day && isDayPast(day),
                        today: day && isDayToday(day),
                        selected: heroSelectedDay === day && !isDayPast(day),
                        selectable: day && !isDayPast(day)
                      }"
                      @click="selectDay(day)"
                    >{{ day || '' }}</div>
                  </div>
                  <div class="mock-hint">Datum auswählen</div>
                </div>

                <!-- Step 1: Time slots -->
                <div v-else-if="heroStep === 1" key="slots" class="mock-slots">
                  <button class="mock-back-btn" @click="heroStep = 0">← Zurück</button>
                  <div class="mock-slots-date">{{ selectedDateLabel }}</div>
                  <div class="mock-slots-grid">
                    <div
                      v-for="slot in timeSlots"
                      :key="slot.time"
                      class="slot-btn"
                      :class="{
                        taken: slot.taken,
                        selected: heroSelectedSlot === slot.time && !slot.taken
                      }"
                      @click="!slot.taken && selectSlot(slot.time)"
                    >
                      {{ slot.time }}
                      <span v-if="slot.taken" class="taken-lbl">Belegt</span>
                    </div>
                  </div>
                  <button
                    v-if="heroSelectedSlot"
                    class="mock-confirm-btn"
                    @click="heroStep = 2"
                  >Termin bestätigen →</button>
                </div>

                <!-- Step 2: Confirmation -->
                <div v-else key="confirm" class="mock-confirm">
                  <div class="confirm-check">✓</div>
                  <div class="confirm-title">Termin bestätigt!</div>
                  <div class="confirm-details">
                    <div class="confirm-item">📅 {{ selectedDateLabel }}</div>
                    <div class="confirm-item">🕐 {{ heroSelectedSlot }} Uhr</div>
                    <div class="confirm-item">📧 Bestätigung gesendet</div>
                    <div class="confirm-item">💬 WhatsApp-Erinnerung aktiv</div>
                  </div>
                  <button class="mock-restart-btn" @click="resetHero">Neue Buchung</button>
                </div>
              </transition>

            </div>
          </div>

          <!-- Floating notification -->
          <transition name="notif-slide">
            <div v-if="heroStep === 2" class="floating-notif">
              <div class="notif-icon">📱</div>
              <div>
                <div class="notif-title">WhatsApp gesendet</div>
                <div class="notif-sub">Erinnerung 24h vorher</div>
              </div>
            </div>
          </transition>

          <!-- Floating Google Calendar badge -->
          <div class="floating-badge">
            <span>📅</span> Google Calendar synced
          </div>
        </div>
      </div>

      <!-- Scroll indicator -->
      <div class="scroll-indicator" @click="scrollTo('features')">
        <div class="scroll-dot"></div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         STATS BAR
    ══════════════════════════════════════════════════ -->
    <section class="stats-bar">
      <div class="container stats-inner">
        <div class="stat-item" v-for="s in stats" :key="s.label">
          <div class="stat-val">{{ s.val }}</div>
          <div class="stat-lab">{{ s.label }}</div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         FEATURES (TABBED)
    ══════════════════════════════════════════════════ -->
    <section id="features" class="features-section">
      <div class="container">
        <div class="section-head">
          <span class="section-tag">Funktionen</span>
          <h2>Alles in einem System</h2>
          <p>Von der Online-Buchung bis zum KI-Telefonassistenten – Ihr komplettes Terminmanagement</p>
        </div>

        <!-- Tab Buttons -->
        <div class="feature-tabs-nav">
          <button
            v-for="tab in featureTabs"
            :key="tab.id"
            class="feature-tab-btn"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            <span class="ftab-icon">{{ tab.icon }}</span>
            <span class="ftab-label">{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <transition name="tab-fade" mode="out-in">
          <div class="feature-tab-panel" :key="activeTab">

            <!-- ──────── BOOKING TAB ──────── -->
            <div v-if="activeTab === 'booking'" class="tab-layout">
              <div class="tab-info">
                <h3>Intuitive Online-Terminbuchung</h3>
                <p>Ihre Kunden buchen rund um die Uhr – ohne Anruf, ohne Wartezeit. Das System prüft Verfügbarkeiten in Echtzeit, versendet automatisch Bestätigungen und erinnert an kommende Termine.</p>
                <ul class="feature-list">
                  <li><span class="fcheck">✓</span> Echtzeit-Verfügbarkeit & Kalender</li>
                  <li><span class="fcheck">✓</span> Multi-Service-Auswahl</li>
                  <li><span class="fcheck">✓</span> Gast- & Kundenbuchung (kein Login nötig)</li>
                  <li><span class="fcheck">✓</span> 1-Click-Stornierung per E-Mail</li>
                  <li><span class="fcheck">✓</span> Automatische Terminerinnerungen</li>
                </ul>
              </div>
              <div class="tab-demo">
                <div class="demo-card">
                  <div class="demo-card-header">Buchungsprozess</div>
                  <div class="booking-steps-row">
                    <div class="bstep" :class="{ done: true }">
                      <div class="bstep-num">1</div>
                      <div class="bstep-label">Datum</div>
                    </div>
                    <div class="bstep-arrow">→</div>
                    <div class="bstep">
                      <div class="bstep-num">2</div>
                      <div class="bstep-label">Service</div>
                    </div>
                    <div class="bstep-arrow">→</div>
                    <div class="bstep">
                      <div class="bstep-num">3</div>
                      <div class="bstep-label">Uhrzeit</div>
                    </div>
                    <div class="bstep-arrow">→</div>
                    <div class="bstep">
                      <div class="bstep-num">4</div>
                      <div class="bstep-label">Confirm</div>
                    </div>
                  </div>
                  <div class="demo-notifs">
                    <div class="demo-notif email-notif">
                      <span>📧</span> E-Mail Bestätigung
                    </div>
                    <div class="demo-notif whatsapp-notif">
                      <span>💬</span> WhatsApp Erinnerung
                    </div>
                    <div class="demo-notif calendar-notif">
                      <span>📅</span> Kalender-Sync
                    </div>
                  </div>
                  <div class="demo-flow-anim">
                    <div class="flow-item" v-for="(item, i) in flowItems" :key="i" :style="{ animationDelay: i * 0.3 + 's' }">
                      <div class="flow-check">✓</div>
                      <div class="flow-text">{{ item }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ──────── AI ASSISTANT TAB ──────── -->
            <div v-if="activeTab === 'ai'" class="tab-layout">
              <div class="tab-info">
                <h3>KI-Telefon-Assistent</h3>
                <p>Kein Anruf bleibt unbeantwortet. Der KI-Assistent nimmt Anrufe entgegen, bucht Termine direkt im System und informiert Sie per Nachricht – 24/7, vollautomatisch.</p>
                <ul class="feature-list">
                  <li><span class="fcheck">✓</span> 24/7 erreichbar – auch nachts & am Wochenende</li>
                  <li><span class="fcheck">✓</span> Natürliche Sprachverarbeitung (NLP)</li>
                  <li><span class="fcheck">✓</span> Automatische Termin-Eintragung</li>
                  <li><span class="fcheck">✓</span> Individuelle Stimme & Branding</li>
                  <li><span class="fcheck">✓</span> Anruf-Protokoll im Dashboard</li>
                </ul>
              </div>
              <div class="tab-demo">
                <div class="phone-mockup">
                  <div class="phone-frame">
                    <div class="phone-notch"></div>
                    <div class="phone-screen">
                      <div class="phone-call-header">
                        <div class="phone-caller-info">
                          <div class="phone-avatar">👤</div>
                          <div>
                            <div class="phone-caller-name">Eingehender Anruf</div>
                            <div class="phone-status">KI-Assistent aktiv</div>
                          </div>
                        </div>
                        <div class="phone-duration">{{ callDuration }}</div>
                      </div>
                      <div class="waveform">
                        <div v-for="i in 24" :key="i" class="wave-bar" :style="getWaveStyle(i)"></div>
                      </div>
                      <div class="ai-transcript">
                        <div
                          v-for="(msg, i) in visibleTranscript"
                          :key="i"
                          class="transcript-msg"
                          :class="msg.role"
                        >
                          <span class="msg-icon">{{ msg.role === 'ai' ? '🤖' : '👤' }}</span>
                          <span class="msg-text">{{ msg.text }}</span>
                        </div>
                        <div v-if="aiTyping" class="transcript-msg ai">
                          <span class="msg-icon">🤖</span>
                          <span class="typing-dots"><span>.</span><span>.</span><span>.</span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ──────── CALENDAR SYNC TAB ──────── -->
            <div v-if="activeTab === 'calendar'" class="tab-layout">
              <div class="tab-info">
                <h3>Google Calendar Integration</h3>
                <p>Alle Termine automatisch synchronisiert – bidirektional, in Echtzeit. Änderungen im Buchungssystem erscheinen sofort in Google Calendar und umgekehrt.</p>
                <ul class="feature-list">
                  <li><span class="fcheck">✓</span> Bidirektionale Echtzeit-Synchronisation</li>
                  <li><span class="fcheck">✓</span> Mehrere Kalender verwaltbar</li>
                  <li><span class="fcheck">✓</span> iCal-Export für Apple Calendar</li>
                  <li><span class="fcheck">✓</span> Automatische Konflikterkennung</li>
                  <li><span class="fcheck">✓</span> OAuth2-sichere Verbindung</li>
                </ul>
              </div>
              <div class="tab-demo">
                <div class="sync-demo">
                  <div class="sync-card">
                    <div class="sync-card-top">
                      <div class="sync-sys-icon">🗓</div>
                      <div class="sync-card-name">Buchungssystem</div>
                    </div>
                    <div class="sync-events">
                      <div class="sync-event blue-ev">Mo 10:00 – Max Müller</div>
                      <div class="sync-event blue-ev">Di 14:30 – Beratungsgespräch</div>
                      <div class="sync-event blue-ev new-ev">Fr 09:00 – Neue Buchung ✨</div>
                    </div>
                  </div>

                  <div class="sync-arrows-col">
                    <div class="sync-arrow right-arr">→</div>
                    <div class="sync-label">SYNC</div>
                    <div class="sync-arrow left-arr">←</div>
                  </div>

                  <div class="sync-card">
                    <div class="sync-card-top">
                      <div class="sync-google-icon">G</div>
                      <div class="sync-card-name">Google Calendar</div>
                    </div>
                    <div class="sync-events">
                      <div class="sync-event green-ev">Mo 10:00 – Max Müller</div>
                      <div class="sync-event green-ev">Di 14:30 – Beratungsgespräch</div>
                      <div class="sync-event green-ev new-ev">Fr 09:00 – Neue Buchung ✨</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ──────── DASHBOARD TAB ──────── -->
            <div v-if="activeTab === 'dashboard'" class="tab-layout">
              <div class="tab-info">
                <h3>Admin Dashboard & Analytics</h3>
                <p>Behalten Sie den Überblick: Umsatz, Auslastung, Kundenstamm und Bewertungen auf einen Blick. Mit Filtern, Datumsbereich-Auswahl und Controlling-Funktionen.</p>
                <ul class="feature-list">
                  <li><span class="fcheck">✓</span> Umsatz- & Auslastungsanalyse</li>
                  <li><span class="fcheck">✓</span> Kundenkartei mit Buchungshistorie</li>
                  <li><span class="fcheck">✓</span> Service-Verwaltung (aktiv/inaktiv)</li>
                  <li><span class="fcheck">✓</span> Öffnungszeiten konfigurierbar</li>
                  <li><span class="fcheck">✓</span> Sondertage & Schließtage</li>
                </ul>
              </div>
              <div class="tab-demo">
                <div class="dashboard-demo">
                  <div class="kpi-row">
                    <div class="kpi-card" v-for="kpi in kpis" :key="kpi.label">
                      <div class="kpi-val">{{ kpi.val }}</div>
                      <div class="kpi-lab">{{ kpi.label }}</div>
                      <div class="kpi-trend">↑ {{ kpi.trend }}</div>
                    </div>
                  </div>
                  <div class="mini-chart">
                    <div class="chart-label">Termine / Woche</div>
                    <div class="chart-bars">
                      <div v-for="(bar, i) in chartBars" :key="i" class="chart-bar-wrap">
                        <div class="chart-bar" :style="{ height: bar.h + '%', animationDelay: i * 0.08 + 's' }"></div>
                        <div class="chart-bar-day">{{ bar.d }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </transition>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         HOW IT WORKS
    ══════════════════════════════════════════════════ -->
    <section class="how-section">
      <div class="container">
        <div class="section-head">
          <span class="section-tag">So einfach geht's</span>
          <h2>In 3 Schritten live</h2>
          <p>Vom ersten Gespräch bis zur fertigen Buchungsseite</p>
        </div>
        <div class="how-steps">
          <div v-for="(step, i) in howSteps" :key="i" class="how-step">
            <div class="how-step-num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="how-step-icon">{{ step.icon }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
            <div v-if="i < howSteps.length - 1" class="how-connector"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         FEATURE CHIPS GRID
    ══════════════════════════════════════════════════ -->
    <section class="chips-section">
      <div class="container">
        <div class="section-head">
          <span class="section-tag">Alle Features</span>
          <h2>Was im System steckt</h2>
        </div>
        <div class="chips-grid">
          <div v-for="chip in featureChips" :key="chip.label" class="chip-card">
            <div class="chip-icon">{{ chip.icon }}</div>
            <div class="chip-label">{{ chip.label }}</div>
            <div class="chip-desc">{{ chip.desc }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         PORTFOLIO / REFERENZEN
    ══════════════════════════════════════════════════ -->
    <section id="portfolio" class="portfolio-section">
      <div class="container">
        <div class="section-head">
          <span class="section-tag">Referenzen</span>
          <h2>Live-Projekte</h2>
          <p>Erfolgreich umgesetzte Buchungssysteme für unsere Kunden</p>
        </div>
        <div class="portfolio-grid">
          <a
            v-for="p in projects"
            :key="p.id"
            class="portfolio-card"
            :href="p.url"
            target="_blank"
            rel="noopener noreferrer"
            @mouseenter="hoveredP = p.id"
            @mouseleave="hoveredP = null"
          >
            <div
              class="portfolio-visual"
              :style="!p.img ? { background: `linear-gradient(135deg, ${p.c1} 0%, ${p.c2} 100%)` } : {}"
            >
              <img v-if="p.img" :src="p.img" :alt="p.title + ' Screenshot'" class="portfolio-img" />
              <div v-else class="portfolio-icon-bg">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <div class="portfolio-overlay" :class="{ vis: hoveredP === p.id }">
                <span class="portfolio-visit-btn">Website besuchen ↗</span>
              </div>
            </div>
            <div class="portfolio-info">
              <div class="portfolio-meta">
                <span class="p-cat">{{ p.category }}</span>
                <span class="p-yr">{{ p.year }}</span>
              </div>
              <h3>{{ p.title }}</h3>
              <p>{{ p.desc }}</p>
              <div class="p-tags">
                <span v-for="t in p.tags" :key="t" class="p-tag">{{ t }}</span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         CTA BAND
    ══════════════════════════════════════════════════ -->
    <section class="cta-band">
      <div class="container cta-inner">
        <div class="cta-text">
          <h2>Bereit für Ihr digitales Terminmanagement?</h2>
          <p>Kostenlose Erstberatung – unverbindlich und innerhalb von 24 Stunden.</p>
        </div>
        <button @click="scrollTo('contact')" class="btn btn-white">
          Jetzt Kontakt aufnehmen
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         CONTACT
    ══════════════════════════════════════════════════ -->
    <section id="contact" class="contact-section">
      <div class="container contact-inner">
        <div class="contact-left">
          <span class="section-tag">Kontakt</span>
          <h2>Projekt anfragen</h2>
          <p>Kostenlose Erstberatung und unverbindliches Angebot innerhalb von 24 Stunden.</p>
          <div class="contact-items">
            <div class="contact-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              <span>Dresden, Sachsen</span>
            </div>
            <div class="contact-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <span>info@nick-volkmann.de</span>
            </div>
            <div class="contact-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <span>0176 / 20074278</span>
            </div>
          </div>
          <div class="trust-row">
            <div class="trust-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              DSGVO-konform
            </div>
            <div class="trust-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Festpreisgarantie
            </div>
            <div class="trust-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Antwort in 24h
            </div>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="contact-form">
          <div class="form-row">
            <div class="form-group">
              <label>Name *</label>
              <input type="text" v-model="form.name" required placeholder="Ihr vollständiger Name">
            </div>
            <div class="form-group">
              <label>Unternehmen *</label>
              <input type="text" v-model="form.company" required placeholder="Ihre Firma">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>E-Mail *</label>
              <input type="email" v-model="form.email" required placeholder="ihre@email.de">
            </div>
            <div class="form-group">
              <label>Telefon</label>
              <input type="tel" v-model="form.phone" placeholder="+49 123 456789">
            </div>
          </div>
          <div class="form-group">
            <label>Interessiert an</label>
            <select v-model="form.service">
              <option value="">Bitte wählen</option>
              <option value="booking">Terminbuchungssystem</option>
              <option value="website">Website Design & Entwicklung</option>
              <option value="complete">Komplettpaket (Website + Terminbuchung)</option>
              <option value="ai">KI-Telefon-Assistent</option>
              <option value="maintenance">Wartung & Support</option>
              <option value="consulting">IT-Beratung</option>
              <option value="other">Sonstiges</option>
            </select>
          </div>
          <div class="form-group">
            <label>Budget (optional)</label>
            <select v-model="form.budget">
              <option value="">Bitte wählen</option>
              <option value="3k">Bis 3.000 €</option>
              <option value="5k">3.000 – 5.000 €</option>
              <option value="10k">5.000 – 10.000 €</option>
              <option value="10k+">Über 10.000 €</option>
            </select>
          </div>
          <div class="form-group">
            <label>Projektbeschreibung *</label>
            <textarea v-model="form.message" rows="4" required placeholder="Beschreiben Sie Ihr Projekt, Ihre Ziele und spezielle Anforderungen..."></textarea>
          </div>
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.privacy" required class="real-checkbox">
              <span class="checkbox-visual" :class="{ checked: form.privacy }">
                <svg v-if="form.privacy" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span>Ich habe die <router-link to="/datenschutz" @click.stop>Datenschutzerklärung</router-link> gelesen und akzeptiere diese *</span>
            </label>
          </div>
          <button type="submit" class="btn btn-primary submit-btn" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Anfrage senden</span>
            <span v-else>Wird gesendet...</span>
            <svg v-if="!isSubmitting" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
          <transition name="msg-fade">
            <div v-if="submitMessage" class="submit-msg" :class="submitStatus">
              {{ submitMessage }}
            </div>
          </transition>
        </form>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════
         FOOTER
    ══════════════════════════════════════════════════ -->
    <footer class="site-footer">
      <div class="container footer-inner">
        <div class="footer-brand">
          <svg viewBox="0 0 40 40" fill="none" width="36" height="36">
            <defs>
              <linearGradient id="f-lg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#667eea"/>
                <stop offset="100%" stop-color="#764ba2"/>
              </linearGradient>
            </defs>
            <path d="M6 8h14l12 12-12 12H6l12-12L6 8z" fill="url(#f-lg)"/>
            <circle cx="35" cy="10" r="2.5" fill="url(#f-lg)"/>
            <circle cx="35" cy="30" r="2.5" fill="url(#f-lg)"/>
          </svg>
          <div>
            <div class="footer-name">Nick Volkmann</div>
            <div class="footer-sub">IT-Beratung · Dresden</div>
          </div>
        </div>

        <div class="footer-links-grid">
          <div class="footer-col">
            <h4>Leistungen</h4>
            <a @click.prevent="scrollTo('features')">Terminbuchungssystem</a>
            <a @click.prevent="scrollTo('features')">KI-Telefon-Assistent</a>
            <a @click.prevent="scrollTo('features')">Google Calendar Sync</a>
            <a @click.prevent="scrollTo('features')">Admin Dashboard</a>
          </div>
          <div class="footer-col">
            <h4>Unternehmen</h4>
            <a @click.prevent="scrollTo('portfolio')">Referenzen</a>
            <a @click.prevent="scrollTo('contact')">Kontakt</a>
          </div>
          <div class="footer-col">
            <h4>Rechtliches</h4>
            <router-link to="/impressum">Impressum</router-link>
            <router-link to="/datenschutz">Datenschutz</router-link>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <p>© {{ new Date().getFullYear() }} Nick Volkmann – IT-Beratung. Alle Rechte vorbehalten.</p>
      </div>
    </footer>

  </div>
</template>

<script>
import { submitContactForm } from '@/apiService';

export default {
  name: 'WelcomeHome',
  data() {
    const now = new Date();
    return {
      // Nav
      isScrolled: false,
      mobileMenuOpen: false,

      // Hero calendar
      heroStep: 0,
      heroMonth: now.getMonth(),
      heroYear: now.getFullYear(),
      heroSelectedDay: null,
      heroSelectedSlot: '',
      weekDays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
      timeSlots: [
        { time: '09:00', taken: false },
        { time: '09:30', taken: false },
        { time: '10:00', taken: true },
        { time: '10:30', taken: false },
        { time: '11:00', taken: false },
        { time: '11:30', taken: true },
        { time: '14:00', taken: false },
        { time: '14:30', taken: false },
        { time: '15:00', taken: false },
        { time: '15:30', taken: false },
      ],

      // AI transcript
      transcriptFull: [
        { role: 'ai', text: 'Guten Tag! Wie kann ich Ihnen helfen?' },
        { role: 'user', text: 'Ich möchte einen Termin buchen.' },
        { role: 'ai', text: 'Gerne! Für welchen Tag hätten Sie Zeit?' },
        { role: 'user', text: 'Am nächsten Dienstag, wenn möglich.' },
        { role: 'ai', text: 'Ich schaue nach freien Slots für Dienstag…' },
      ],
      visibleTranscript: [],
      aiTyping: false,
      transcriptTimer: null,
      callTimer: null,
      callSeconds: 0,

      // Tabs
      activeTab: 'booking',
      featureTabs: [
        { id: 'booking', icon: '🗓', label: 'Terminbuchung' },
        { id: 'ai', icon: '🤖', label: 'KI-Assistent' },
        { id: 'calendar', icon: '📅', label: 'Google Calendar' },
        { id: 'dashboard', icon: '📊', label: 'Dashboard' },
      ],

      // Stats
      stats: [
        { val: '2+', label: 'Live-Projekte' },
        { val: '100%', label: 'Kundenzufriedenheit' },
        { val: '24/7', label: 'Verfügbarkeit' },
        { val: '< 4 Wo.', label: 'Time-to-Live' },
      ],

      // Dashboard demo
      kpis: [
        { val: '€ 4.280', label: 'Umsatz / Monat', trend: '+12%' },
        { val: '134', label: 'Termine', trend: '+8%' },
        { val: '92%', label: 'Auslastung', trend: '+3%' },
      ],
      chartBars: [
        { h: 55, d: 'Mo' },
        { h: 78, d: 'Di' },
        { h: 62, d: 'Mi' },
        { h: 90, d: 'Do' },
        { h: 48, d: 'Fr' },
        { h: 35, d: 'Sa' },
        { h: 20, d: 'So' },
      ],

      // Booking flow animation items
      flowItems: [
        'Termin bestätigt um 14:30',
        'E-Mail-Bestätigung gesendet',
        'WhatsApp-Erinnerung geplant',
        'Zahlung über PayPal erfolgreich',
      ],

      // How it works
      howSteps: [
        {
          icon: '💬',
          title: 'Beratung & Konzept',
          desc: 'Wir analysieren Ihre Anforderungen und erstellen ein maßgeschneidertes Konzept für Ihr Terminbuchungssystem.',
        },
        {
          icon: '⚙️',
          title: 'Entwicklung & Setup',
          desc: 'Ihr System wird entwickelt, konfiguriert und mit Ihren Tools (Google Calendar, WhatsApp, PayPal) verbunden.',
        },
        {
          icon: '🚀',
          title: 'Go-Live & Support',
          desc: 'Nach dem Launch erhalten Sie vollständige Einweisung und laufenden Support für Ihr Buchungssystem.',
        },
      ],

      // Feature chips
      featureChips: [
        { icon: '🗓', label: 'Online-Terminbuchung', desc: 'Echtzeit-Verfügbarkeit, Kalenderansicht' },
        { icon: '🤖', label: 'KI-Telefonassistent', desc: '24/7 automatische Anrufannahme' },
        { icon: '📅', label: 'Google Calendar Sync', desc: 'Bidirektionale Synchronisation' },
        { icon: '💬', label: 'WhatsApp-Erinnerungen', desc: 'Automatische Terminerinnerungen' },
        { icon: '📧', label: 'E-Mail-Benachrichtigungen', desc: 'Professionelle Transaktionsmails' },
        { icon: '💳', label: 'PayPal & Stripe', desc: 'Sichere Online-Zahlungen' },
        { icon: '📊', label: 'Admin Dashboard', desc: 'Analytics, KPIs, Kundenverwaltung' },
        { icon: '🔒', label: 'JWT-Authentifizierung', desc: 'Sichere Nutzerverwaltung' },
        { icon: '📱', label: 'Mobile-First Design', desc: '100% responsiv für alle Geräte' },
        { icon: '🌍', label: 'Custom Domain & Hosting', desc: 'SSL, CDN, High-Availability' },
        { icon: '📋', label: 'Service-Verwaltung', desc: 'Preise, Dauer, aktiv/inaktiv' },
        { icon: '❌', label: '1-Click-Stornierung', desc: 'Automatische Rückerstattung' },
      ],

      // Portfolio
      hoveredP: null,
      projects: [
        {
          id: 1,
          title: 'Salongstudio',
          category: 'Beauty & Lifestyle',
          year: '2025',
          url: 'https://salongstudio.de/',
          desc: 'Professionelle Barbershop-Plattform mit vollintegriertem Terminbuchungssystem, Online-Zahlungen, WhatsApp-Erinnerungen und KI-Telefonassistent.',
          tags: ['Terminbuchung', 'WhatsApp', 'PayPal', 'KI-Assistent', 'Dashboard'],
          img: '/screenshots/salongstudio.jpg',
          c1: '#f093fb',
          c2: '#f5576c',
        },
        {
          id: 2,
          title: 'Pimp My Hair',
          category: 'Beauty & Lifestyle',
          year: '2025',
          url: 'https://pimpmyhair.de/',
          desc: 'Stilvolle Website mit Service-Katalog, Stylist-Auswahl, automatischen Erinnerungen und nahtloser Booking-Experience.',
          tags: ['Design', 'Terminbuchung', 'PayPal', 'E-Mail'],
          img: '/screenshots/pimpmyhair.jpg',
          c1: '#fa709a',
          c2: '#fee140',
        },
      ],

      // Contact
      form: {
        name: '', company: '', email: '', phone: '',
        service: '', budget: '', message: '', privacy: false,
      },
      isSubmitting: false,
      submitMessage: '',
      submitStatus: '',
    };
  },

  computed: {
    heroMonthLabel() {
      const d = new Date(this.heroYear, this.heroMonth, 1);
      return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    },
    calendarDays() {
      const year = this.heroYear;
      const month = this.heroMonth;
      const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      // Monday-first offset
      const offset = (firstDay === 0 ? 6 : firstDay - 1);
      const cells = [];
      for (let i = 0; i < offset; i++) cells.push(null);
      for (let d = 1; d <= daysInMonth; d++) cells.push(d);
      return cells;
    },
    selectedDateLabel() {
      if (!this.heroSelectedDay) return '';
      const d = new Date(this.heroYear, this.heroMonth, this.heroSelectedDay);
      return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },
    callDuration() {
      const m = Math.floor(this.callSeconds / 60).toString().padStart(2, '0');
      const s = (this.callSeconds % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },
  },

  mounted() {
    window.addEventListener('scroll', this.onScroll);
    this.startAiTranscript();
    this.startCallTimer();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    clearInterval(this.transcriptTimer);
    clearInterval(this.callTimer);
  },

  watch: {
    activeTab(val) {
      if (val === 'ai') {
        this.visibleTranscript = [];
        this.aiTyping = false;
        clearInterval(this.transcriptTimer);
        this.startAiTranscript();
      }
    },
  },

  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 60;
    },
    toggleMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    nav(section) {
      this.mobileMenuOpen = false;
      this.scrollTo(section);
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },

    // Hero calendar
    prevMonth() {
      if (this.heroMonth === 0) { this.heroMonth = 11; this.heroYear--; }
      else this.heroMonth--;
      this.heroSelectedDay = null;
    },
    nextMonth() {
      const now = new Date();
      const maxMonth = new Date(now.getFullYear(), now.getMonth() + 3, 1);
      const current = new Date(this.heroYear, this.heroMonth + 1, 1);
      if (current < maxMonth) {
        if (this.heroMonth === 11) { this.heroMonth = 0; this.heroYear++; }
        else this.heroMonth++;
        this.heroSelectedDay = null;
      }
    },
    isDayPast(day) {
      const now = new Date();
      const d = new Date(this.heroYear, this.heroMonth, day);
      d.setHours(23, 59, 59);
      return d < now;
    },
    isDayToday(day) {
      const now = new Date();
      return (
        day === now.getDate() &&
        this.heroMonth === now.getMonth() &&
        this.heroYear === now.getFullYear()
      );
    },
    selectDay(day) {
      if (!day || this.isDayPast(day)) return;
      this.heroSelectedDay = day;
      this.heroSelectedSlot = '';
      setTimeout(() => { this.heroStep = 1; }, 200);
    },
    selectSlot(time) {
      this.heroSelectedSlot = time;
    },
    resetHero() {
      this.heroStep = 0;
      this.heroSelectedDay = null;
      this.heroSelectedSlot = '';
    },

    // AI transcript animation
    startAiTranscript() {
      let i = 0;
      this.transcriptTimer = setInterval(() => {
        if (i < this.transcriptFull.length) {
          if (this.transcriptFull[i].role === 'ai') this.aiTyping = true;
          setTimeout(() => {
            this.aiTyping = false;
            if (i < this.transcriptFull.length) {
              this.visibleTranscript.push(this.transcriptFull[i]);
              i++;
            }
          }, this.transcriptFull[i]?.role === 'ai' ? 800 : 200);
        } else {
          clearInterval(this.transcriptTimer);
          setTimeout(() => {
            this.visibleTranscript = [];
            this.startAiTranscript();
          }, 4000);
        }
      }, 1800);
    },

    startCallTimer() {
      this.callTimer = setInterval(() => {
        this.callSeconds++;
      }, 1000);
    },

    getWaveStyle(i) {
      const heights = [12, 20, 35, 28, 45, 38, 55, 42, 60, 48, 52, 38, 45, 32, 55, 40, 28, 48, 35, 22, 45, 30, 18, 38];
      return {
        height: heights[i - 1] + 'px',
        animationDelay: (i * 0.05) + 's',
      };
    },

    // Contact form
    async handleSubmit() {
      this.isSubmitting = true;
      this.submitMessage = '';
      this.submitStatus = '';
      try {
        const response = await submitContactForm({
          name: this.form.name,
          company: this.form.company,
          email: this.form.email,
          phone: this.form.phone,
          service: this.form.service,
          budget: this.form.budget,
          message: this.form.message,
          privacy: this.form.privacy,
        });
        if (response.success) {
          this.submitMessage = response.message || 'Vielen Dank! Ich melde mich innerhalb von 24 Stunden.';
          this.submitStatus = 'success';
          this.form = { name: '', company: '', email: '', phone: '', service: '', budget: '', message: '', privacy: false };
          setTimeout(() => { this.submitMessage = ''; this.submitStatus = ''; }, 8000);
        }
      } catch (error) {
        if (error.response?.status === 400 && error.response?.data?.errors) {
          // Backend-Validierungsfehler (z. B. privacy_accepted, company, message zu kurz)
          const msgs = Object.values(error.response.data.errors).flat();
          this.submitMessage = msgs.join(' – ');
        } else if (error.response?.status === 429) {
          this.submitMessage = 'Zu viele Anfragen. Bitte versuchen Sie es in einer Stunde erneut.';
        } else if (error.response?.status >= 500) {
          this.submitMessage = 'Serverfehler. Bitte kontaktieren Sie mich telefonisch.';
        } else if (error.message?.includes('Network Error')) {
          this.submitMessage = 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
        } else {
          this.submitMessage = 'Fehler beim Senden. Bitte versuchen Sie es später erneut.';
        }
        this.submitStatus = 'error';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style>
/* ═══════════════════════════════════════════════
   RESET & TOKENS
═══════════════════════════════════════════════ */
*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

:root {
  --primary: #667eea;
  --secondary: #764ba2;
  --dark: #0d0d1a;
  --text: #1e293b;
  --muted: #64748b;
  --light: #f8fafc;
  --border: #e2e8f0;
  --success: #10b981;
  --radius: 16px;
  --shadow: 0 4px 24px rgba(0,0,0,0.08);
  --shadow-lg: 0 12px 48px rgba(0,0,0,0.14);
  --gradient: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
}

html { scroll-behavior: smooth; }

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: var(--text);
  background: #fff;
  overflow-x: hidden;
  line-height: 1.6;
}

::selection { background: var(--primary); color: #fff; }

::-webkit-scrollbar { width: 8px; }
::-webkit-scrollbar-track { background: #f1f1f1; }
::-webkit-scrollbar-thumb { background: var(--gradient); border-radius: 8px; }

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ═══════════════════════════════════════════════
   NAVIGATION
═══════════════════════════════════════════════ */
.nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  background: rgba(255,255,255,0.97);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 2px 24px rgba(0,0,0,0.08);
  transition: background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
}
.nav.scrolled {
  background: rgba(255,255,255,0.97);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 2px 24px rgba(0,0,0,0.10);
}
.nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
}
.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: default;
}
.nav-logo-svg { width: 36px; height: 36px; flex-shrink: 0; }
.nav-logo-name {
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
  transition: color 0.3s;
}
.nav.scrolled .nav-logo-name { color: var(--text); }
.nav-logo-sub {
  font-size: 11px;
  font-weight: 500;
  color: var(--muted);
  transition: color 0.3s;
}
.nav.scrolled .nav-logo-sub { color: var(--muted); }

.nav-links {
  display: flex;
  align-items: center;
  gap: 36px;
}
.nav-links a {
  color: var(--text);
  text-decoration: none;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  transition: color 0.3s;
  letter-spacing: 0.01em;
}
.nav.scrolled .nav-links a {
  color: var(--text);
}
.nav-links a:hover { opacity: 0.75; }
.nav.scrolled .nav-links a:hover { opacity: 0.75; }
.nav-links a::after {
  content: '';
  position: absolute;
  bottom: -4px; left: 0;
  width: 0; height: 2px;
  background: var(--gradient);
  border-radius: 2px;
  transition: width 0.3s;
}
.nav-links a:hover::after { width: 100%; }

.nav-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: var(--gradient);
  color: #fff !important;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.25s;
  box-shadow: 0 4px 15px rgba(102,126,234,0.35);
}
.nav-cta-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 25px rgba(102,126,234,0.45); }
.nav-cta-btn::after { display: none !important; }

/* Hamburger */
.hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}
.hamburger span {
  width: 24px; height: 2px;
  background: var(--text);
  border-radius: 2px;
  transition: all 0.3s;
  display: block;
}
.nav.scrolled .hamburger span { background: var(--text); }
.hamburger.open span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
.hamburger.open span:nth-child(2) { opacity: 0; }
.hamburger.open span:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }

/* ═══════════════════════════════════════════════
   BUTTONS
═══════════════════════════════════════════════ */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.25s;
  text-decoration: none;
}
.btn-primary {
  background: var(--gradient);
  color: #fff;
  box-shadow: 0 8px 28px rgba(102,126,234,0.4);
}
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 40px rgba(102,126,234,0.5); }
.btn-ghost {
  background: rgba(255,255,255,0.12);
  color: #fff;
  border: 1.5px solid rgba(255,255,255,0.3);
  backdrop-filter: blur(8px);
}
.btn-ghost:hover { background: rgba(255,255,255,0.2); }
.btn-white {
  background: #fff;
  color: var(--primary);
  box-shadow: 0 6px 24px rgba(0,0,0,0.15);
}
.btn-white:hover { transform: translateY(-2px); box-shadow: 0 12px 35px rgba(0,0,0,0.2); }

/* ═══════════════════════════════════════════════
   SECTION COMMON
═══════════════════════════════════════════════ */
.section-head {
  text-align: center;
  margin-bottom: 60px;
}
.section-tag {
  display: inline-block;
  background: var(--gradient);
  color: #fff;
  padding: 6px 18px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 16px;
}
.section-head h2 {
  font-size: 40px;
  font-weight: 800;
  color: var(--text);
  margin-bottom: 14px;
  letter-spacing: -0.5px;
}
.section-head p {
  font-size: 17px;
  color: var(--muted);
  max-width: 560px;
  margin: 0 auto;
}

/* ═══════════════════════════════════════════════
   HERO
═══════════════════════════════════════════════ */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 120px 0 80px;
  overflow: hidden;
}
.hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(140deg, #0d0d1a 0%, #1a1040 50%, #0d1a2a 100%);
  z-index: -1;
}
.hero-grid-overlay {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(102,126,234,0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(102,126,234,0.06) 1px, transparent 1px);
  background-size: 48px 48px;
  animation: gridDrift 30s linear infinite;
}
@keyframes gridDrift {
  0% { transform: translate(0,0); }
  100% { transform: translate(48px, 48px); }
}
.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.25;
}
.glow-1 {
  width: 600px; height: 600px;
  background: radial-gradient(circle, #667eea 0%, transparent 70%);
  top: -200px; left: -100px;
  animation: glowPulse 8s ease-in-out infinite;
}
.glow-2 {
  width: 500px; height: 500px;
  background: radial-gradient(circle, #764ba2 0%, transparent 70%);
  bottom: -150px; right: -50px;
  animation: glowPulse 10s ease-in-out infinite reverse;
}
@keyframes glowPulse {
  0%, 100% { opacity: 0.20; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(1.1); }
}

.hero-inner {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 64px;
  align-items: center;
  width: 100%;
}

/* Hero Text */
.hero-text { color: #fff; }
.available-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(16,185,129,0.15);
  border: 1px solid rgba(16,185,129,0.35);
  color: #34d399;
  padding: 7px 16px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 24px;
}
.badge-pulse {
  width: 8px; height: 8px;
  background: #34d399;
  border-radius: 50%;
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(52,211,153,0.4); }
  50% { opacity: 0.8; transform: scale(1.1); box-shadow: 0 0 0 6px rgba(52,211,153,0); }
}

.hero-h1 {
  font-size: 58px;
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: -1.5px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.hero-accent {
  background: linear-gradient(90deg, #a78bfa, #818cf8, #60a5fa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.hero-sub {
  font-size: 17px;
  color: rgba(255,255,255,0.72);
  line-height: 1.75;
  margin-bottom: 36px;
  max-width: 480px;
}
.hero-actions {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}
.hero-pills {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.pill {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.8);
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
}

/* Hero Mockup */
.hero-mockup {
  position: relative;
}
.mockup-window {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 0 24px 80px rgba(0,0,0,0.5);
}
.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  background: rgba(255,255,255,0.04);
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.dot { width: 12px; height: 12px; border-radius: 50%; }
.dot-r { background: #ff5f57; }
.dot-y { background: #febc2e; }
.dot-g { background: #28c840; }
.mockup-bar-title {
  margin-left: 8px;
  font-size: 13px;
  color: rgba(255,255,255,0.5);
  font-weight: 500;
}
.mockup-body { padding: 24px; min-height: 340px; }

/* Mini Calendar in Hero */
.mock-cal { color: #fff; }
.mock-cal-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.mock-cal-title { font-size: 15px; font-weight: 700; }
.cal-nav-btn {
  background: rgba(255,255,255,0.1);
  border: none;
  color: #fff;
  width: 28px; height: 28px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.2s;
}
.cal-nav-btn:hover { background: rgba(255,255,255,0.2); }
.mock-cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 6px;
}
.mock-cal-weekdays span {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.4);
  padding: 4px 0;
}
.mock-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}
.cal-cell {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.85);
  transition: all 0.15s;
}
.cal-cell.empty { pointer-events: none; }
.cal-cell.past { color: rgba(255,255,255,0.2); }
.cal-cell.today {
  background: rgba(102,126,234,0.25);
  border: 1px solid rgba(102,126,234,0.5);
  color: #a78bfa;
  font-weight: 700;
}
.cal-cell.selectable { cursor: pointer; }
.cal-cell.selectable:hover { background: rgba(102,126,234,0.2); }
.cal-cell.selected {
  background: var(--gradient) !important;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(102,126,234,0.5);
}
.mock-hint {
  text-align: center;
  font-size: 12px;
  color: rgba(255,255,255,0.35);
  margin-top: 14px;
}

/* Time slots */
.mock-slots { color: #fff; }
.mock-back-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 12px;
  transition: color 0.2s;
  display: block;
}
.mock-back-btn:hover { color: #fff; }
.mock-slots-date {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  color: rgba(255,255,255,0.85);
}
.mock-slots-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}
.slot-btn {
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.85);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  position: relative;
}
.slot-btn:hover:not(.taken) { background: rgba(102,126,234,0.2); border-color: rgba(102,126,234,0.4); }
.slot-btn.taken {
  opacity: 0.35;
  cursor: not-allowed;
  text-decoration: line-through;
}
.slot-btn.selected {
  background: var(--gradient);
  border-color: transparent;
  box-shadow: 0 4px 14px rgba(102,126,234,0.4);
  color: #fff;
}
.taken-lbl {
  display: block;
  font-size: 10px;
  opacity: 0.6;
  font-weight: 400;
  text-decoration: none;
}
.mock-confirm-btn {
  width: 100%;
  padding: 12px;
  background: var(--gradient);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 6px 20px rgba(102,126,234,0.4);
}
.mock-confirm-btn:hover { transform: translateY(-1px); }

/* Confirmation */
.mock-confirm {
  text-align: center;
  color: #fff;
  padding: 20px 0;
}
.confirm-check {
  width: 60px; height: 60px;
  background: linear-gradient(135deg, #10b981, #34d399);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 16px;
  box-shadow: 0 8px 28px rgba(16,185,129,0.4);
  animation: confirmPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes confirmPop {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.confirm-title { font-size: 20px; font-weight: 800; margin-bottom: 16px; }
.confirm-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  background: rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 16px;
}
.confirm-item { font-size: 14px; color: rgba(255,255,255,0.85); }
.mock-restart-btn {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  padding: 10px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}
.mock-restart-btn:hover { background: rgba(255,255,255,0.18); }

/* Floating elements */
.floating-notif {
  position: absolute;
  bottom: -20px;
  left: -30px;
  background: #fff;
  border-radius: 14px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.2);
  font-size: 13px;
}
.notif-icon { font-size: 24px; }
.notif-title { font-weight: 700; color: var(--text); font-size: 13px; }
.notif-sub { color: var(--muted); font-size: 12px; }
.floating-badge {
  position: absolute;
  top: -16px;
  right: -20px;
  background: #fff;
  border-radius: 50px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  box-shadow: 0 8px 28px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  gap: 6px;
  animation: floatY 4s ease-in-out infinite;
}
@keyframes floatY {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* Scroll indicator */
.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px; height: 56px;
  border: 2px solid rgba(255,255,255,0.25);
  border-radius: 18px;
}
.scroll-dot {
  width: 6px; height: 6px;
  background: #fff;
  border-radius: 50%;
  animation: scrollDot 2s infinite;
}
@keyframes scrollDot {
  0% { transform: translateY(-8px); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateY(8px); opacity: 0; }
}

/* ════════════════════════════��══════════════════
   STATS BAR
═══════════════════════════════════════════════ */
.stats-bar {
  background: #fff;
  border-bottom: 1px solid var(--border);
  padding: 40px 0;
}
.stats-inner {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
}
.stat-item {
  text-align: center;
  padding: 0 20px;
  border-right: 1px solid var(--border);
}
.stat-item:last-child { border-right: none; }
.stat-val {
  font-size: 36px;
  font-weight: 900;
  background: var(--gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
}
.stat-lab { font-size: 14px; color: var(--muted); font-weight: 500; }

/* ═══════════════════════════════════════════════
   FEATURES (TABBED)
═══════════════════════════════════════════════ */
.features-section {
  padding: 100px 0;
  background: var(--light);
}

.feature-tabs-nav {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 48px;
  flex-wrap: wrap;
}
.feature-tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 12px;
  border: 1.5px solid var(--border);
  background: #fff;
  color: var(--muted);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
}
.feature-tab-btn:hover { border-color: var(--primary); color: var(--primary); }
.feature-tab-btn.active {
  background: var(--gradient);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 6px 20px rgba(102,126,234,0.35);
}
.ftab-icon { font-size: 18px; }
.ftab-label { font-size: 14px; }

/* Tab panel layout */
.feature-tab-panel {
  background: #fff;
  border-radius: 24px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.tab-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 420px;
}
.tab-info {
  padding: 56px 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.tab-info h3 {
  font-size: 28px;
  font-weight: 800;
  color: var(--text);
  margin-bottom: 16px;
  letter-spacing: -0.3px;
}
.tab-info p {
  font-size: 16px;
  color: var(--muted);
  line-height: 1.75;
  margin-bottom: 28px;
}
.feature-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.feature-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: var(--text);
  font-weight: 500;
}
.fcheck {
  width: 22px; height: 22px;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}
.tab-demo {
  background: var(--light);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

/* Booking Demo Card */
.demo-card {
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  box-shadow: var(--shadow);
  width: 100%;
  max-width: 360px;
}
.demo-card-header {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--muted);
  margin-bottom: 20px;
}
.booking-steps-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.bstep {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.bstep-num {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: var(--light);
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}
.bstep.done .bstep-num {
  background: var(--gradient);
  color: #fff;
}
.bstep-label { font-size: 10px; color: var(--muted); font-weight: 500; }
.bstep-arrow { color: var(--border); font-size: 14px; flex-shrink: 0; }
.demo-notifs {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.demo-notif {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.email-notif { background: #eff6ff; color: #1d4ed8; }
.whatsapp-notif { background: #f0fdf4; color: #15803d; }
.calendar-notif { background: #fdf4ff; color: #7e22ce; }
.demo-flow-anim {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.flow-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--light);
  border-radius: 10px;
  animation: slideInL 0.4s ease-out both;
}
@keyframes slideInL {
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: translateX(0); }
}
.flow-check {
  width: 24px; height: 24px;
  background: var(--success);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.flow-text { font-size: 13px; font-weight: 500; color: var(--text); }

/* Phone Mockup (AI Tab) */
.phone-mockup {
  display: flex;
  justify-content: center;
}
.phone-frame {
  width: 260px;
  background: #1a1a2e;
  border-radius: 40px;
  padding: 16px 12px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.4);
  position: relative;
  border: 3px solid #2a2a4a;
}
.phone-notch {
  width: 80px; height: 24px;
  background: #0d0d1a;
  border-radius: 12px;
  margin: 0 auto 12px;
}
.phone-screen {
  background: #0d0d1a;
  border-radius: 28px;
  padding: 16px;
  min-height: 360px;
}
.phone-call-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.phone-caller-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.phone-avatar {
  width: 36px; height: 36px;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.phone-caller-name { font-size: 13px; font-weight: 700; color: #fff; }
.phone-status { font-size: 11px; color: #34d399; }
.phone-duration { font-size: 13px; color: rgba(255,255,255,0.5); font-variant-numeric: tabular-nums; }

/* Waveform */
.waveform {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 60px;
  margin: 12px 0;
}
.wave-bar {
  width: 3px;
  background: var(--gradient);
  border-radius: 3px;
  animation: waveDance 1.2s ease-in-out infinite alternate;
  min-height: 4px;
}
@keyframes waveDance {
  from { transform: scaleY(0.3); }
  to { transform: scaleY(1); }
}

/* AI Transcript */
.ai-transcript {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}
.transcript-msg {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  animation: fadeInMsg 0.3s ease-out;
}
@keyframes fadeInMsg {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
.msg-icon { font-size: 14px; flex-shrink: 0; margin-top: 2px; }
.msg-text {
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  line-height: 1.5;
}
.transcript-msg.ai .msg-text {
  background: rgba(102,126,234,0.2);
  color: #c4b5fd;
  border-radius: 2px 12px 12px 12px;
}
.transcript-msg.user .msg-text {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.8);
  border-radius: 12px 2px 12px 12px;
}
.typing-dots span {
  display: inline-block;
  animation: typingBounce 1.2s infinite;
  font-size: 18px;
  color: #a78bfa;
}
.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typingBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

/* Calendar Sync Demo */
.sync-demo {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
}
.sync-card {
  flex: 1;
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
}
.sync-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}
.sync-sys-icon, .sync-google-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.sync-sys-icon { background: #eff6ff; }
.sync-google-icon {
  background: #fff;
  border: 1.5px solid var(--border);
  font-size: 16px;
  font-weight: 900;
  color: #4285f4;
}
.sync-events { display: flex; flex-direction: column; gap: 6px; }
.sync-event {
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
}
.blue-ev { background: #eff6ff; color: #1d4ed8; }
.green-ev { background: #f0fdf4; color: #15803d; }
.new-ev { animation: newEventPop 0.5s ease-out; }
@keyframes newEventPop {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}
.sync-arrows-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.sync-label { font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: 1px; }
.sync-arrow {
  font-size: 20px;
  font-weight: 700;
  animation: arrowPulse 2s ease-in-out infinite;
}
.right-arr { color: var(--primary); }
.left-arr { color: var(--secondary); animation-delay: 1s; }
@keyframes arrowPulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* Dashboard Demo */
.dashboard-demo { width: 100%; max-width: 380px; }
.kpi-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}
.kpi-card {
  background: #fff;
  border-radius: 14px;
  padding: 14px 12px;
  box-shadow: var(--shadow);
  text-align: center;
}
.kpi-val { font-size: 17px; font-weight: 800; color: var(--text); margin-bottom: 2px; }
.kpi-lab { font-size: 11px; color: var(--muted); font-weight: 500; margin-bottom: 4px; }
.kpi-trend { font-size: 12px; color: var(--success); font-weight: 700; }
.mini-chart { background: #fff; border-radius: 14px; padding: 18px; box-shadow: var(--shadow); }
.chart-label { font-size: 12px; font-weight: 700; color: var(--muted); margin-bottom: 14px; text-transform: uppercase; letter-spacing: 0.5px; }
.chart-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 6px;
  height: 80px;
}
.chart-bar-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  height: 100%;
  justify-content: flex-end;
}
.chart-bar {
  width: 100%;
  background: var(--gradient);
  border-radius: 4px 4px 0 0;
  animation: barGrow 0.8s ease-out both;
}
@keyframes barGrow {
  from { height: 0 !important; }
}
.chart-bar-day { font-size: 10px; color: var(--muted); font-weight: 600; }

/* ═══════════════════════════════════════════════
   HOW IT WORKS
═══════════════════════════════════════════════ */
.how-section {
  padding: 100px 0;
  background: var(--dark);
}
.how-section .section-head h2 { color: #fff; }
.how-section .section-head p { color: rgba(255,255,255,0.6); }
.how-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  position: relative;
}
.how-step {
  padding: 40px 36px;
  position: relative;
  text-align: center;
}
.how-step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 60px;
  right: -1px;
  width: 2px;
  height: 60px;
  background: linear-gradient(to bottom, rgba(102,126,234,0.5), transparent);
}
.how-step-num {
  font-size: 56px;
  font-weight: 900;
  background: var(--gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 8px;
  opacity: 0.35;
}
.how-step-icon {
  font-size: 40px;
  margin-bottom: 16px;
}
.how-step h3 {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 12px;
}
.how-step p {
  font-size: 15px;
  color: rgba(255,255,255,0.55);
  line-height: 1.7;
}

/* ═══════════════════════════════════════════════
   FEATURE CHIPS
═══════════════════════════════════════════════ */
.chips-section {
  padding: 100px 0;
  background: #fff;
}
.chips-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.chip-card {
  background: var(--light);
  border: 1.5px solid var(--border);
  border-radius: 16px;
  padding: 24px 20px;
  transition: all 0.25s;
  cursor: default;
}
.chip-card:hover {
  border-color: var(--primary);
  box-shadow: 0 8px 28px rgba(102,126,234,0.12);
  transform: translateY(-3px);
}
.chip-icon { font-size: 28px; margin-bottom: 10px; }
.chip-label { font-size: 14px; font-weight: 700; color: var(--text); margin-bottom: 4px; }
.chip-desc { font-size: 12px; color: var(--muted); line-height: 1.5; }

/* ═══════════════════════════════════════════════
   PORTFOLIO
═══════════════════════════════════════════════ */
.portfolio-section {
  padding: 100px 0;
  background: var(--light);
}
.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
}
.portfolio-card {
  display: block;
  text-decoration: none;
  color: inherit;
  background: #fff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: all 0.3s;
}
.portfolio-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-lg);
}
.portfolio-visual {
  height: 260px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.portfolio-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
  transition: transform 0.4s ease;
}
.portfolio-card:hover .portfolio-img { transform: scale(1.04); }
.portfolio-icon-bg { opacity: 0.4; }
.portfolio-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}
.portfolio-overlay.vis { opacity: 1; }
.portfolio-visit-btn {
  background: #fff;
  color: var(--text);
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.2s;
  pointer-events: none;
}
.portfolio-card:hover .portfolio-visit-btn { transform: scale(1.04); box-shadow: 0 6px 20px rgba(255,255,255,0.25); }
.portfolio-info { padding: 28px; }
.portfolio-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.p-cat { font-size: 12px; font-weight: 700; color: var(--primary); text-transform: uppercase; letter-spacing: 0.5px; }
.p-yr { font-size: 12px; color: var(--muted); font-weight: 500; }
.portfolio-info h3 { font-size: 22px; font-weight: 800; color: var(--text); margin-bottom: 10px; }
.portfolio-info p { font-size: 14px; color: var(--muted); line-height: 1.7; margin-bottom: 16px; }
.p-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.p-tag {
  background: var(--light);
  color: var(--text);
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

/* ═══════════════════════════════════════════════
   CTA BAND
═══════════════════════════════════════════════ */
.cta-band {
  background: var(--gradient);
  padding: 80px 0;
}
.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  flex-wrap: wrap;
}
.cta-text h2 { font-size: 32px; font-weight: 800; color: #fff; margin-bottom: 8px; }
.cta-text p { font-size: 17px; color: rgba(255,255,255,0.8); }

/* ═══════════════════════════════════════════════
   CONTACT
═══════════════════════════════════════════════ */
.contact-section {
  padding: 100px 0;
  background: var(--light);
}
.contact-inner {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 60px;
  align-items: start;
}
.contact-left .section-tag { margin-bottom: 16px; }
.contact-left h2 { font-size: 36px; font-weight: 800; color: var(--text); margin-bottom: 12px; }
.contact-left > p { font-size: 16px; color: var(--muted); margin-bottom: 32px; line-height: 1.7; }
.contact-items { display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px; }
.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 14px;
  box-shadow: var(--shadow);
  font-size: 15px;
  color: var(--text);
  font-weight: 500;
}
.contact-item svg { stroke: var(--primary); flex-shrink: 0; }
.trust-row { display: flex; gap: 12px; flex-wrap: wrap; }
.trust-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: var(--shadow);
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}
.trust-item svg { stroke: var(--success); }

/* Contact Form */
.contact-form {
  background: #fff;
  border-radius: 24px;
  padding: 40px;
  box-shadow: var(--shadow-lg);
}
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.form-group { margin-bottom: 20px; }
.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid var(--border);
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: var(--text);
  background: #fff;
  transition: all 0.25s;
  appearance: none;
}
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 4px rgba(102,126,234,0.1);
}
.form-group textarea { resize: vertical; }
.checkbox-group { margin-top: 4px; }
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 13px;
  color: var(--muted);
  line-height: 1.5;
}
.real-checkbox {
  /* Visuell versteckt, aber für Browser/Label-Click-Forwarding aktiv */
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
.checkbox-visual {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border);
  border-radius: 6px;
  flex-shrink: 0;
  margin-top: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: #fff;
  cursor: pointer;
}
.checkbox-visual.checked {
  background: var(--gradient);
  border-color: transparent;
}
.checkbox-label a { color: var(--primary); }
.checkbox-label span:last-child {
  margin-left: 7px;   /* 4–8px je nach Geschmack */
}
.submit-btn {
  width: 100%;
  justify-content: center;
  font-size: 16px;
  padding: 16px;
  margin-top: 8px;
  border-radius: 14px;
}
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }
.submit-msg {
  margin-top: 16px;
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}
.submit-msg.success { background: #d1fae5; color: #065f46; }
.submit-msg.error { background: #fee2e2; color: #991b1b; }

/* ═══════════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════════ */
.site-footer {
  background: var(--dark);
  color: #fff;
  padding: 60px 0 0;
}
.footer-inner {
  display: grid;
  grid-template-columns: 1.2fr 2fr;
  gap: 60px;
  padding-bottom: 48px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.footer-brand { display: flex; align-items: flex-start; gap: 14px; }
.footer-name { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
.footer-sub { font-size: 13px; color: rgba(255,255,255,0.5); }
.footer-links-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
}
.footer-col h4 { font-size: 14px; font-weight: 700; margin-bottom: 18px; color: rgba(255,255,255,0.9); }
.footer-col a {
  display: block;
  font-size: 14px;
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  margin-bottom: 10px;
  cursor: pointer;
  transition: color 0.2s;
}
.footer-col a:hover { color: #fff; }
.footer-bottom {
  text-align: center;
  padding: 24px;
  font-size: 13px;
  color: rgba(255,255,255,0.35);
}

/* ═══════════════════════════════════════════════
   TRANSITIONS
═══════════════════════════════════════════════ */
.tab-fade-enter-active,
.tab-fade-leave-active { transition: all 0.3s ease; }
.tab-fade-enter-from { opacity: 0; transform: translateY(12px); }
.tab-fade-leave-to { opacity: 0; transform: translateY(-8px); }

.step-fade-enter-active,
.step-fade-leave-active { transition: all 0.25s ease; }
.step-fade-enter-from { opacity: 0; transform: scale(0.97); }
.step-fade-leave-to { opacity: 0; transform: scale(1.02); }

.notif-slide-enter-active,
.notif-slide-leave-active { transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1); }
.notif-slide-enter-from { opacity: 0; transform: translateX(-20px) scale(0.9); }
.notif-slide-leave-to { opacity: 0; transform: translateX(-20px); }

.msg-fade-enter-active,
.msg-fade-leave-active { transition: all 0.3s ease; }
.msg-fade-enter-from { opacity: 0; transform: translateY(-6px); }
.msg-fade-leave-to { opacity: 0; }

/* ═══════════════════════════════════════════════
   RESPONSIVE
═══════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .hero-inner { grid-template-columns: 1fr; }
  .hero-mockup { display: none; }
  .tab-layout { grid-template-columns: 1fr; }
  .tab-demo { padding: 24px; }
  .chips-grid { grid-template-columns: repeat(3, 1fr); }
  .contact-inner { grid-template-columns: 1fr; }
  .footer-inner { grid-template-columns: 1fr; gap: 40px; }
  .how-steps { grid-template-columns: 1fr; }
  .how-step::after { display: none; }
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
    position: fixed;
    top: 70px; left: 0; right: 0;
    background: rgba(13,13,26,0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    gap: 0;
    padding: 16px 0 24px;
  }
  .nav-links.open { display: flex; }
  .nav-links a {
    padding: 14px 28px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    opacity: 1;
  }
  .nav-links a:hover { opacity: 0.75; }
  .nav-links a::after { display: none; }
  .nav-cta-btn { margin: 12px 20px 0; }
  .hamburger { display: flex; }

  .hero-h1 { font-size: 38px; }
  .hero-sub { font-size: 15px; }
  .hero-actions { flex-direction: column; }
  .btn { width: 100%; justify-content: center; }
  .hero { padding: 100px 0 60px; min-height: auto; }

  .stats-inner { grid-template-columns: repeat(2, 1fr); }
  .stat-item:nth-child(2) { border-right: none; }

  .features-section { padding: 70px 0; }
  .feature-tabs-nav { gap: 6px; }
  .feature-tab-btn { padding: 10px 14px; }
  .ftab-label { display: none; }
  .ftab-icon { font-size: 22px; }
  .tab-info { padding: 32px 24px; }
  .tab-info h3 { font-size: 22px; }

  .section-head h2 { font-size: 30px; }
  .chips-grid { grid-template-columns: repeat(2, 1fr); }
  .portfolio-grid { grid-template-columns: 1fr; }
  .portfolio-section { padding: 70px 0; }
  .portfolio-visual { height: 220px; }
  .kpi-row { grid-template-columns: repeat(3, 1fr); }
  .kpi-val { font-size: 14px; }
  .sync-demo { flex-direction: column; }
  .sync-arrows-col { flex-direction: row; }
  .cta-inner { flex-direction: column; text-align: center; }
  .cta-text h2 { font-size: 26px; }
  .form-row { grid-template-columns: 1fr; }
  .contact-form { padding: 28px 20px; }
  .footer-links-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 480px) {
  .container { padding: 0 16px; }
  .hero-h1 { font-size: 30px; }
  .hero-pills { display: none; }
  .stats-inner { grid-template-columns: repeat(2, 1fr); gap: 0; }
  .stat-val { font-size: 28px; }
  .chips-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .section-head h2 { font-size: 26px; }
  .footer-links-grid { grid-template-columns: 1fr; gap: 24px; }
  .portfolio-section { padding: 60px 0; }
  .portfolio-visual { height: 200px; }
  .portfolio-info { padding: 20px; }
  .portfolio-info h3 { font-size: 18px; }
  .p-tags { gap: 6px; }
  .p-tag { font-size: 11px; padding: 4px 10px; }
  .contact-section { padding: 60px 0; }
  .contact-form { padding: 24px 16px; }
  .cta-band { padding: 50px 0; }
  .section-head { margin-bottom: 36px; }
  .nav-inner { padding: 14px 16px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
